import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { VISIBLE_FISHES, clamp, type FishStats, getGrowthStage, FISH_GROWTH_CONFIG } from '@/data/fish'
import { DECORATIONS, type Decoration, type PlacedDecoration } from '@/data/decorations'
import { useSaveSystem } from './useSaveSystem'
import { DEFAULT_SETTINGS, type SaveData } from '@/data/save'

export type Lighting = 'day' | 'night'

export interface BackgroundState {
  enabled: boolean
  imageData: string | null
  presetId: string | null
  fitMode: 'cover' | 'contain' | 'fill'
  opacity: number
}

const DEFAULT_BACKGROUND: BackgroundState = {
  enabled: false,
  imageData: null,
  presetId: null,
  fitMode: 'cover',
  opacity: 0.8,
}

interface XpState {
  level: number
  xp: number
  needed: number
}

interface EconomyState {
  coins: number
  lastLoginReward: string
}

export type FeedType = 'normal' | 'highProtein' | 'colorEnhance' | 'probiotic' | 'golden'

export interface Feed {
  id: FeedType
  name: string
  price: number
  icon: string
  description: string
  effect: { satiety: number; mood: number; energy: number }
  special?: string
}

export const FEEDS: Record<FeedType, Feed> = {
  normal: {
    id: 'normal',
    name: '普通颗粒',
    price: 0,
    icon: '🫘',
    description: '基础饲料，满足日常需求',
    effect: { satiety: 55, mood: 12, energy: 8 },
  },
  highProtein: {
    id: 'highProtein',
    name: '高蛋白饲料',
    price: 15,
    icon: '🥩',
    description: '富含蛋白质，促进成长',
    effect: { satiety: 80, mood: 20, energy: 15 },
    special: '亲密度额外+5',
  },
  colorEnhance: {
    id: 'colorEnhance',
    name: '增色饲料',
    price: 25,
    icon: '🎨',
    description: '天然色素，让鱼更鲜艳',
    effect: { satiety: 60, mood: 35, energy: 10 },
    special: '鱼更活泼',
  },
  probiotic: {
    id: 'probiotic',
    name: '益生菌饲料',
    price: 35,
    icon: '🦠',
    description: '调节肠道，改善消化',
    effect: { satiety: 70, mood: 25, energy: 25 },
    special: '水质消耗减半(10分钟)',
  },
  golden: {
    id: 'golden',
    name: '黄金饲料',
    price: 50,
    icon: '🌟',
    description: '传说级饲料，效果惊人',
    effect: { satiety: 100, mood: 50, energy: 30 },
    special: '所有效果翻倍',
  },
}

const xpNeeded = (level: number) => 800 + (level - 1) * 400

export function useAquarium() {
  const { save, load, deleteSave } = useSaveSystem()

  const [settings, setSettings] = useState<SaveData['settings']>(DEFAULT_SETTINGS)
  const [statsMap, setStatsMap] = useState<Record<string, FishStats>>(() =>
    Object.fromEntries(VISIBLE_FISHES.map((f) => [f.id, { ...f.base }])),
  )
  const [water, setWater] = useState(860)
  const [xpState, setXpState] = useState<XpState>({ level: 12, xp: 450, needed: xpNeeded(12) })
  const [lighting, setLighting] = useState<Lighting>('day')
  const [levelUpTick, setLevelUpTick] = useState(0)
  const [soundTrigger, setSoundTrigger] = useState(0)
  const [soundType, setSoundType] = useState<string>('')
  const [economy, setEconomy] = useState<EconomyState>({ coins: 100, lastLoginReward: '' })
  const [unlockedFish, setUnlockedFish] = useState<Set<string>>(
    new Set(VISIBLE_FISHES.filter(f => !f.unlockCondition).map(f => f.id)),
  )
  const [cleanCount, setCleanCount] = useState(0)
  const [placedDecorations, setPlacedDecorations] = useState<PlacedDecoration[]>([])
  const [purchasedDecorations, setPurchasedDecorations] = useState<Set<string>>(new Set())
  const [ambientSoundId, setAmbientSoundId] = useState<'none' | 'river' | 'forest' | 'gentle'>('none')
  const [backgroundState, setBackgroundState] = useState<BackgroundState>(() => {
    const saved = localStorage.getItem('aquarium_background')
    if (saved) {
      try {
        return JSON.parse(saved) as BackgroundState
      } catch {
        return DEFAULT_BACKGROUND
      }
    }
    return DEFAULT_BACKGROUND
  })

  useEffect(() => {
    localStorage.setItem('aquarium_background', JSON.stringify(backgroundState))
  }, [backgroundState])

  useEffect(() => {
    const savedData = load()
    if (savedData) {
      const mergedStats = { ...statsMap }
      let changed = false
      for (const [id, savedStats] of Object.entries(savedData.fish)) {
        if (!mergedStats[id]) {
          changed = true
        }
        mergedStats[id] = {
          satiety: savedStats.satiety ?? mergedStats[id]?.satiety ?? 600,
          mood: savedStats.mood ?? mergedStats[id]?.mood ?? 600,
          bond: savedStats.bond ?? mergedStats[id]?.bond ?? 300,
          energy: savedStats.energy ?? mergedStats[id]?.energy ?? 600,
          growthStage: savedStats.growthStage ?? mergedStats[id]?.growthStage ?? 0,
          totalExperience: savedStats.totalExperience ?? mergedStats[id]?.totalExperience ?? 0,
        }
      }
      if (changed) {
        setStatsMap(mergedStats)
      }
      setWater(savedData.water)
      setXpState(savedData.player)
      setLighting(savedData.lighting)
      setEconomy(savedData.economy)
      setUnlockedFish(new Set(Object.keys(mergedStats).filter(id => savedData.fish[id]?.unlocked)))
      setCleanCount(savedData.cleanCount)
      setPlacedDecorations(savedData.placedDecorations)
      setPurchasedDecorations(new Set(savedData.purchasedDecorations))
      setSettings(savedData.settings || DEFAULT_SETTINGS)
      if (savedData.ambientSoundId) {
        setAmbientSoundId(savedData.ambientSoundId)
      }
    }
  }, [load])
  const statsRef = useRef(statsMap)
  statsRef.current = statsMap

  useEffect(() => {
    localStorage.setItem('aquarium_unlocked', JSON.stringify([...unlockedFish]))
  }, [unlockedFish])

  useEffect(() => {
    localStorage.setItem('aquarium_clean_count', cleanCount.toString())
  }, [cleanCount])

  useEffect(() => {
    localStorage.setItem('aquarium_decorations', JSON.stringify(placedDecorations))
  }, [placedDecorations])

  useEffect(() => {
    localStorage.setItem('aquarium_purchased_decorations', JSON.stringify([...purchasedDecorations]))
  }, [purchasedDecorations])

  const saveGame = useCallback(() => {
    const saveData: SaveData = {
      version: '1.0.0',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      economy,
      player: xpState,
      fish: Object.fromEntries(
        Object.entries(statsMap).map(([id, stats]) => [
          id,
          { ...stats, unlocked: unlockedFish.has(id) },
        ]),
      ),
      water,
      lighting,
      placedDecorations,
      purchasedDecorations: [...purchasedDecorations],
      cleanCount,
      ambientSoundId,
      settings,
    }
    save(saveData)
  }, [save, economy, xpState, statsMap, unlockedFish, water, lighting, placedDecorations, purchasedDecorations, cleanCount, ambientSoundId, settings])

  useEffect(() => {
    if (!settings.autoSaveEnabled) return
    const interval = setInterval(() => {
      saveGame()
    }, settings.saveInterval * 1000)
    return () => clearInterval(interval)
  }, [saveGame, settings.autoSaveEnabled, settings.saveInterval])

  useEffect(() => {
    const handleBeforeUnload = () => {
      saveGame()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [saveGame])

  const playSound = useCallback((type: string) => {
    setSoundType(type)
    setSoundTrigger((t) => t + 1)
  }, [])

  const addCoins = useCallback((amount: number) => {
    setEconomy((prev) => {
      const newCoins = prev.coins + amount
      return { ...prev, coins: newCoins }
    })
    playSound('coin')
  }, [playSound])

  const spendCoins = useCallback((amount: number) => {
    return new Promise<boolean>((resolve) => {
      setEconomy((prev) => {
        if (prev.coins >= amount) {
          resolve(true)
          return { ...prev, coins: prev.coins - amount }
        }
        resolve(false)
        return prev
      })
    })
  }, [])

  const updateAmbientSound = useCallback((soundId: 'none' | 'river' | 'forest' | 'gentle') => {
    setAmbientSoundId(soundId)
  }, [])

  const updateBackground = useCallback((state: Partial<BackgroundState>) => {
    setBackgroundState((prev) => {
      const next = { ...prev, ...state }
      return next
    })
  }, [])

  const checkUnlock = useCallback(() => {
    let unlocked = false
    const totalBond = Object.values(statsMap).reduce((sum, s) => sum + s.bond, 0)
    
    for (const fish of VISIBLE_FISHES) {
      if (unlockedFish.has(fish.id) || !fish.unlockCondition) continue
      
      const { type, value } = fish.unlockCondition
      let conditionMet = false
      
      switch (type) {
        case 'level':
          conditionMet = xpState.level >= value
          break
        case 'bond':
          conditionMet = totalBond >= value
          break
        case 'clean':
          conditionMet = cleanCount >= value
          break
      }
      
      if (conditionMet) {
        setUnlockedFish((prev) => new Set([...prev, fish.id]))
        playSound('positive')
        unlocked = true
      }
    }
    return unlocked
  }, [statsMap, xpState.level, cleanCount, unlockedFish, playSound])

  const isFishUnlocked = useCallback((fishId: string) => {
    return unlockedFish.has(fishId)
  }, [unlockedFish])

  const getUnlockedFishes = useCallback(() => {
    return VISIBLE_FISHES.filter(f => unlockedFish.has(f.id))
  }, [unlockedFish])

  const buyDecoration = useCallback(async (decorationId: string) => {
    const decoration = DECORATIONS.find(d => d.id === decorationId)
    if (!decoration) return false
    
    if (purchasedDecorations.has(decorationId)) return true
    
    const success = await spendCoins(decoration.price)
    if (success) {
      setPurchasedDecorations((prev) => new Set([...prev, decorationId]))
      playSound('coin')
    }
    return success
  }, [purchasedDecorations, spendCoins, playSound])

  const placeDecoration = useCallback((decorationId: string, x: number, y: number) => {
    if (!purchasedDecorations.has(decorationId)) return false
    
    const placed: PlacedDecoration = {
      decorationId,
      x,
      y,
      rotation: Math.random() * 20 - 10,
    }
    setPlacedDecorations((prev) => [...prev, placed])
    return true
  }, [purchasedDecorations])

  const removeDecoration = useCallback((index: number) => {
    setPlacedDecorations((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const getDecorationById = useCallback((id: string): Decoration | undefined => {
    return DECORATIONS.find(d => d.id === id)
  }, [])

  const getDecorationBonuses = useCallback(() => {
    let moodBonus = 0
    let energyBonus = 0
    
    for (const placed of placedDecorations) {
      const decoration = DECORATIONS.find(d => d.id === placed.decorationId)
      if (decoration) {
        moodBonus += decoration.effect.moodBonus ?? 0
        energyBonus += decoration.effect.energyBonus ?? 0
      }
    }
    
    return { moodBonus, energyBonus }
  }, [placedDecorations])

  const loginRewardChecked = useRef(false)

  const checkLoginReward = useCallback(() => {
    if (loginRewardChecked.current) return false
    
    const today = new Date().toISOString().split('T')[0]
    if (economy.lastLoginReward !== today) {
      loginRewardChecked.current = true
      setEconomy((prev) => ({
        ...prev,
        coins: prev.coins + 15,
        lastLoginReward: today,
      }))
      playSound('coin')
      return true
    }
    loginRewardChecked.current = true
    return false
  }, [economy.lastLoginReward, playSound])

  useEffect(() => {
    localStorage.setItem('aquarium_economy', JSON.stringify(economy))
  }, [economy])

  const gainXp = useCallback((amount: number) => {
    setXpState((prev) => {
      let { level, xp } = prev
      let needed = prev.needed
      xp += amount
      let leveled = false
      while (xp >= needed) {
        xp -= needed
        level += 1
        needed = xpNeeded(level)
        leveled = true
      }
      if (leveled) setTimeout(() => setLevelUpTick((t) => t + 1), 0)
      return { level, xp, needed }
    })
  }, [])

  const patchStats = useCallback((fishId: string, patch: Partial<FishStats>) => {
    setStatsMap((prev) => {
      const cur = prev[fishId]
      const next: FishStats = {
        satiety: clamp(cur.satiety + (patch.satiety ?? 0)),
        mood: clamp(cur.mood + (patch.mood ?? 0)),
        bond: clamp(cur.bond + (patch.bond ?? 0)),
        energy: clamp(cur.energy + (patch.energy ?? 0)),
        growthStage: patch.growthStage ?? cur.growthStage,
        totalExperience: patch.totalExperience ?? cur.totalExperience,
      }
      return { ...prev, [fishId]: next }
    })
  }, [])

  // 每吃掉一颗饲料
  const onEat = useCallback(
    (fishId: string, feedType: FeedType = 'normal') => {
      const feed = FEEDS[feedType]
      const growthStage = statsMap[fishId]?.growthStage ?? 0
      const feedingModifier = 1 + growthStage * FISH_GROWTH_CONFIG.weightModifiers.feeding
      
      patchStats(fishId, {
        satiety: Math.floor(feed.effect.satiety * feedingModifier),
        mood: Math.floor(feed.effect.mood * feedingModifier),
        energy: Math.floor(feed.effect.energy * feedingModifier),
      })
      if (feedType === 'highProtein') {
        patchStats(fishId, { bond: Math.floor(5 * feedingModifier) })
      }
      gainXp(20)
      addCoins(2)
      playSound('eat')
    },
    [patchStats, gainXp, addCoins, playSound, statsMap],
  )

  // 逗鱼(点击/拍打水面)
  const onTease = useCallback(
    (fishId: string) => {
      patchStats(fishId, { mood: 35, bond: 18, energy: -10 })
      gainXp(15)
      addCoins(3)
      playSound('splash')
    },
    [patchStats, gainXp, addCoins, playSound],
  )

  // 逗鱼模式:持续跟随,亲密度缓慢增长
  const onPetTrickle = useCallback(
    (fishId: string) => {
      patchStats(fishId, { bond: 4, mood: 3, energy: -2 })
    },
    [patchStats],
  )

  // 换水
  const onClean = useCallback(() => {
    setWater(1000)
    gainXp(60)
    addCoins(10)
    setCleanCount((c) => c + 1)
    playSound('positive')
  }, [gainXp, addCoins, playSound])

  // 光影秀
  const onLightShow = useCallback(
    (fishId: string) => {
      patchStats(fishId, { mood: 90, energy: -15 })
      gainXp(25)
      playSound('positive')
    },
    [patchStats, gainXp, playSound],
  )

  const toggleLighting = useCallback(() => {
    setLighting((l) => (l === 'day' ? 'night' : 'day'))
  }, [])

  // 状态随时间自然衰减
  useEffect(() => {
    const id = setInterval(() => {
      setStatsMap((prev) => {
        const next: typeof prev = {}
        for (const [k, s] of Object.entries(prev)) {
          next[k] = {
            satiety: clamp(s.satiety - 12),
            mood: clamp(s.mood - (s.satiety < 250 ? 16 : 7)),
            bond: clamp(s.bond - 2),
            energy: clamp(s.energy + (s.satiety > 400 ? 6 : -8)),
            growthStage: s.growthStage,
            totalExperience: s.totalExperience,
          }
        }
        return next
      })
      setWater((w) => clamp(w - 8))
    }, 15000)
    return () => clearInterval(id)
  }, [])

  const [fishLevelUpTick, setFishLevelUpTick] = useState(0)

  useEffect(() => {
    const newStage = getGrowthStage(xpState.level)
    
    setStatsMap((prev) => {
      const next: typeof prev = {}
      let changed = false
      
      for (const [k, s] of Object.entries(prev)) {
        if (s.growthStage !== newStage) {
          changed = true
          next[k] = {
            ...s,
            growthStage: newStage,
          }
        } else {
          next[k] = s
        }
      }
      
      if (changed) {
        setTimeout(() => {
          setFishLevelUpTick((t) => t + 1)
          setTimeout(() => setFishLevelUpTick(0), 3000)
        }, 0)
        playSound('positive')
      }
      
      return next
    })
  }, [xpState.level, playSound])

  const getFishGrowthStage = useCallback((fishId: string): number => {
    return statsMap[fishId]?.growthStage ?? 0
  }, [statsMap])

  const getFishGrowthScale = useCallback((fishId: string): number => {
    const stage = statsMap[fishId]?.growthStage ?? 0
    return FISH_GROWTH_CONFIG.scales[Math.min(stage, FISH_GROWTH_CONFIG.scales.length - 1)]
  }, [statsMap])

  const api = useMemo(
    () => ({
      statsMap,
      water,
      xpState,
      lighting,
      levelUpTick,
      fishLevelUpTick,
      soundTrigger,
      soundType,
      economy,
      unlockedFish,
      cleanCount,
      placedDecorations,
      purchasedDecorations,
      settings,
      backgroundState,
      ambientSoundId,
      onEat,
      onTease,
      onPetTrickle,
      onClean,
      onLightShow,
      toggleLighting,
      playSound,
      addCoins,
      spendCoins,
      checkLoginReward,
      checkUnlock,
      isFishUnlocked,
      getUnlockedFishes,
      buyDecoration,
      placeDecoration,
      removeDecoration,
      getDecorationById,
      getDecorationBonuses,
      getFishGrowthStage,
      getFishGrowthScale,
      saveGame,
      deleteSave,
      setSettings,
      updateBackground,
      updateAmbientSound,
    }),
    [statsMap, water, xpState, lighting, levelUpTick, fishLevelUpTick, soundTrigger, soundType, economy, unlockedFish, cleanCount, placedDecorations, purchasedDecorations, settings, backgroundState, ambientSoundId, onEat, onTease, onPetTrickle, onClean, onLightShow, toggleLighting, playSound, addCoins, spendCoins, checkLoginReward, checkUnlock, isFishUnlocked, getUnlockedFishes, buyDecoration, placeDecoration, removeDecoration, getDecorationById, getDecorationBonuses, getFishGrowthStage, getFishGrowthScale, saveGame, deleteSave, setSettings, updateBackground, updateAmbientSound],
  )

  return api
}

export type AquariumApi = ReturnType<typeof useAquarium>
